// Rink geometry builders. Every dimension comes from sim tuning (and the
// faceoff spots come from `faceoffSpotInto`) so the rendered rink cannot
// drift from the rules.
//
// World mapping (see the migration plan): three.x = sim.y (width, ±15),
// three.y = up, three.z = -sim.x (length; home goal at +z, near the
// default camera). All geometry here is built directly in that frame.
//
// Everything is built once at startup; nothing in this module runs per
// frame.

import { tuning } from "@arcade-hockey/sim";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import {
  BoxGeometry,
  BufferGeometry,
  Float32BufferAttribute,
  Shape,
  ShapeGeometry,
} from "three/webgpu";

const HALF_LENGTH = tuning.rink.length / 2; // 30 — sim x extent → three ∓z
const HALF_WIDTH = tuning.rink.width / 2; // 15 — sim y extent → three ±x
const CORNER_R = tuning.rink.cornerRadius;

/**
 * The rounded-rect rink outline in the XZ plane (y = 0), as a closed Shape
 * in (x, z) coordinates. Shared by the ice slab, the board wall, and the
 * trim rails so they always agree.
 */
function rinkOutline(inset = 0): Shape {
  const hw = HALF_WIDTH - inset;
  const hl = HALF_LENGTH - inset;
  const r = Math.max(0.01, CORNER_R - inset);
  const s = new Shape();
  // Counterclockwise in shape coords (x → three x, y → three -z). Each
  // corner is a true circular arc (hockey corners), not a bezier.
  s.moveTo(hw, -(hl - r));
  s.lineTo(hw, hl - r);
  s.absarc(hw - r, hl - r, r, 0, Math.PI / 2, false);
  s.lineTo(-(hw - r), hl);
  s.absarc(-(hw - r), hl - r, r, Math.PI / 2, Math.PI, false);
  s.lineTo(-hw, -(hl - r));
  s.absarc(-(hw - r), -(hl - r), r, Math.PI, Math.PI * 1.5, false);
  s.lineTo(hw - r, -hl);
  s.absarc(hw - r, -(hl - r), r, Math.PI * 1.5, Math.PI * 2, false);
  s.closePath();
  return s;
}

/**
 * Ice surface: a rounded-rect plane at y = 0. The geometry is built in the
 * shape's local (x, y) frame and rotated flat; the ice material reads
 * marking positions from `positionLocal.xy` which stays in that frame —
 * local x = three x = sim y, local y = -three z = sim x.
 */
export function buildIceGeometry(): BufferGeometry {
  const geo = new ShapeGeometry(rinkOutline(), 24);
  // Lay flat: shape (x, y) → world (x, -z) with +shapeY toward -z (away end).
  geo.rotateX(-Math.PI / 2);
  return geo;
}

/**
 * Board glass wall: a vertical ribbon following the rink outline.
 * UV.x runs 0→1 along the path (for end-to-end tinting), UV.y runs 0→1
 * bottom→top. Normals point inward (toward the ice) so refraction offsets
 * read correctly from inside-the-rink camera angles; the material renders
 * double-sided.
 */
export function buildBoardWallGeometry(height: number, segments = 160): BufferGeometry {
  const points = rinkOutline().getSpacedPoints(segments);
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i <= segments; i++) {
    const p = points[i % segments];
    if (p === undefined) continue;
    const next = points[(i + 1) % segments];
    const prev = points[(i - 1 + segments) % segments];
    // Tangent from neighbors; inward normal = tangent rotated, pointing at origin.
    let nx = 0;
    let nz = 0;
    if (next !== undefined && prev !== undefined) {
      const tx = next.x - prev.x;
      const tz = next.y - prev.y;
      const len = Math.hypot(tx, tz) || 1;
      nx = -tz / len;
      nz = tx / len;
      // Flip outward-facing normals inward (toward the rink center).
      if (nx * p.x + nz * p.y > 0) {
        nx = -nx;
        nz = -nz;
      }
    }
    positions.push(p.x, 0, p.y, p.x, height, p.y);
    normals.push(nx, 0, nz, nx, 0, nz);
    const u = i / segments;
    uvs.push(u, 0, u, 1);
    if (i < segments) {
      const a = i * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
  }

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.setAttribute("normal", new Float32BufferAttribute(normals, 3));
  geo.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  return geo;
}

/**
 * Neon trim rails: thin square-section ribbons hugging the board wall at
 * the top edge and at the ice line. Same outline path → same UV.x for the
 * end-to-end color gradient.
 */
export function buildTrimRailsGeometry(boardHeight: number, railRadius: number): BufferGeometry {
  const top = buildRailRing(boardHeight, railRadius);
  const base = buildRailRing(railRadius, railRadius);
  const merged = mergeGeometries([top, base]);
  top.dispose();
  base.dispose();
  return merged;
}

function buildRailRing(centerY: number, radius: number): BufferGeometry {
  const segments = 160;
  const points = rinkOutline(radius).getSpacedPoints(segments);
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  // Square cross-section around the path: offsets along (path normal, up).
  // A flat-shaded box ribbon reads fine once bloom rounds it out.
  const offsets: Array<[number, number]> = [
    [radius, radius], // outer-top
    [-radius, radius], // inner-top
    [-radius, -radius], // inner-bottom
    [radius, -radius], // outer-bottom
  ];
  const ringCount = offsets.length;
  for (let i = 0; i <= segments; i++) {
    const p = points[i % segments];
    const next = points[(i + 1) % segments];
    const prev = points[(i - 1 + segments) % segments];
    if (p === undefined || next === undefined || prev === undefined) continue;
    // Path normal in the XZ plane, oriented outward (away from the origin).
    const tx = next.x - prev.x;
    const tz = next.y - prev.y;
    const tlen = Math.hypot(tx, tz) || 1;
    let ox = -tz / tlen;
    let oz = tx / tlen;
    if (ox * p.x + oz * p.y < 0) {
      ox = -ox;
      oz = -oz;
    }
    const u = i / segments;
    for (const [ro, yo] of offsets) {
      positions.push(p.x + ox * ro, centerY + yo, p.y + oz * ro);
      uvs.push(u, 0.5);
    }
    if (i < segments) {
      const a = i * ringCount;
      for (let k = 0; k < ringCount; k++) {
        const k2 = (k + 1) % ringCount;
        indices.push(
          a + k,
          a + ringCount + k,
          a + k2,
          a + k2,
          a + ringCount + k,
          a + ringCount + k2,
        );
      }
    }
  }
  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geo.computeVertexNormals();
  return geo;
}

/**
 * One goal: a neon frame box outlining the goal mouth volume, open toward
 * the rink. Returns geometry centered on the goal-line midpoint at the
 * HOME end (+z); the away goal is the same mesh rotated 180° about Y.
 * Frame bars are thin boxes merged into one geometry.
 */
export function buildGoalFrameGeometry(frameRadius: number): BufferGeometry {
  const halfW = tuning.rink.goalMouthHalfWidth;
  const depth = tuning.rink.goalDepth;
  const height = 0.95; // visual goal height — presentation-only, no sim meaning
  const r = frameRadius;

  const bars: BufferGeometry[] = [];
  const bar = (
    sx: number,
    sy: number,
    sz: number,
    px: number,
    py: number,
    pz: number,
  ): BufferGeometry => {
    const b = new BoxGeometry(sx + r * 2, sy + r * 2, sz + r * 2);
    b.translate(px, py, pz);
    return b;
  };

  // Goal line plane (z = 0 local, rink side); net recedes to +depth (out of rink).
  // Front uprights + crossbar on the goal line:
  bars.push(bar(0, height, 0, -halfW, height / 2, 0));
  bars.push(bar(0, height, 0, halfW, height / 2, 0));
  bars.push(bar(halfW * 2, 0, 0, 0, height, 0));
  // Back frame:
  bars.push(bar(0, height, 0, -halfW, height / 2, depth));
  bars.push(bar(0, height, 0, halfW, height / 2, depth));
  bars.push(bar(halfW * 2, 0, 0, 0, height, depth));
  // Side connectors, top and bottom:
  bars.push(bar(0, 0, depth, -halfW, height, depth / 2));
  bars.push(bar(0, 0, depth, halfW, height, depth / 2));
  bars.push(bar(0, 0, depth, -halfW, 0, depth / 2));
  bars.push(bar(0, 0, depth, halfW, 0, depth / 2));
  // Bottom bar at the back:
  bars.push(bar(halfW * 2, 0, 0, 0, 0, depth));

  const merged = mergeGeometries(bars);
  for (const b of bars) b.dispose();
  return merged;
}

/** Sim-frame goal-line center of the HOME goal, in three world coords. */
export const HOME_GOAL_Z = HALF_LENGTH - tuning.rink.goalLineOffset; // +z toward camera
